import { Injectable } from "@angular/core";

import * as XLSX from 'xlsx';

import { DateTime } from "luxon";

import { ExcelService } from 'dspx-core';

import { ExcelSheetColumnDataType, ExpectedSheetConfiguration } from "../interfaces/expected-sheet-configuration.interface";
import { ExcelSheetColumnValidationErrorType, ParsedExcelSheet } from "../interfaces/parsed-excel-sheet.interface";
import { DataTable } from "../interfaces/data-table.interface";

export type SheetData = string | ArrayBuffer;

@Injectable({
  providedIn: 'root'
})
export class ExcelParserService {

  constructor(
    private excelService: ExcelService) {
  }

  /**
   * Parses an Excel workbook file and returns the data
   * @param data file data containing the Excel workbook
   * @returns object containing the parsed data
   */
  parseWorkBook(data: SheetData): DataTable[] {
    const xlsxWorkSheets = this.getXlsxWorkSheets(data);
    const dataTables = xlsxWorkSheets
      .map(xlsxWorkSheet => ({
        tableName: xlsxWorkSheet.xlsxWorkSheetName,
        data: xlsxWorkSheet.xlsxWorkSheetData
      }));
    return dataTables;
  }

  /**
   * Parses an Excel spreadsheet and returns useful information on how to validate and read the data
   * @param data file data containing the spreadsheet
   * @param expectedHeaders array of column headers expected to be present in the first row of the spreadsheet (the assumption being that the actual data starts from the second row)
   * @returns object containing useful information on how to validate and read the data
   */
  parseSheet(data: SheetData, expectedConfiguration: ExpectedSheetConfiguration): ParsedExcelSheet {

    const result: ParsedExcelSheet = { expectedConfiguration };

    const xlsxWorkSheet = this.getXlsxWorkSheet(data);
    if (!xlsxWorkSheet) {
      return result;
    }

    expectedConfiguration ??= { columns: [] };
    expectedConfiguration.columns ??= [];
    const expectedHeaders = expectedConfiguration.columns.map(column => column.headerName);

    // Calculate result.actualLastRowNumber
    const cellRange = xlsxWorkSheet['!ref']; // e.g.: "A1:BJ25"
    const actualLastCellName = cellRange?.split(':')?.[1]; // e.g.: "BJ25"
    const actualLastColumnName = actualLastCellName?.replace(/[0-9]/g, '');  // e.g.: "BJ"
    const actualLastRowName = actualLastCellName?.replace(/[A-Z]/g, ''); // e.g.: "25"
    result.actualLastRowNumber = actualLastRowName && Number(actualLastRowName);

    const expectedColumnCount = expectedHeaders.length;
    if (expectedColumnCount <= 0) {
      return result;
    }

    // e.g.: ['A', 'B', 'C', ..., 'Z', 'AA', 'AB', 'AC', ..., 'AZ', 'BA', 'BB', 'BC', ..., 'BJ']
    const expectedHeaderColumnNames = this.excelService.getExcelStyleColumnNamesFromColumnCount(expectedColumnCount);

    // Calculate result.hasExpectedHeader
    const getCellValueByCellName = (cellName: string): string => xlsxWorkSheet[cellName]?.v?.toString()?.trim();
    for (let i = 0; i < expectedColumnCount; i++) {
      const actualHeader = getCellValueByCellName(`${expectedHeaderColumnNames[i]}1`);
      const expectedHeader = expectedHeaders[i];
      if (actualHeader?.toUpperCase() !== expectedHeader?.toUpperCase()) {
        result.hasExpectedHeader = false;
        return result;
      }
    }
    result.hasExpectedHeader = true;

    // Calculate result.hasOnlyExpectedHeader
    const expectedLastHeaderColumnName = expectedHeaderColumnNames[expectedColumnCount - 1];
    result.hasOnlyExpectedHeader = (actualLastColumnName === expectedLastHeaderColumnName);

    // Calculate result.hasData
    result.hasData = result.actualLastRowNumber && result.actualLastRowNumber > 1;
    if (!result.hasData) {
      return result;
    }

    // Calculate result.getCellValue
    const headerNameToColumnNameMap = new Map(expectedHeaderColumnNames.map((columnName, i) => [expectedHeaders[i], columnName]));
    const getCellName = (headerName: string, rowNumber: number): string => `${headerNameToColumnNameMap.get(headerName)}${rowNumber}`;
    result.getCellValue = (headerName: string, rowNumber: number): string => getCellValueByCellName(getCellName(headerName, rowNumber));

    // Calculate result.firstColumnValidationError
    for (let i = 0; i < expectedColumnCount; i++) {
      const columnConfiguration = expectedConfiguration.columns[i];
      if (columnConfiguration.isMandatory || columnConfiguration.dataType) {
        const headerName = expectedHeaders[i];
        for (let rowNumber = 2; rowNumber <= result.actualLastRowNumber; rowNumber++) {
          const cellValue = result.getCellValue(headerName, rowNumber);
          if (columnConfiguration.isMandatory && !cellValue) {
            result.firstColumnValidationError = { errorType: ExcelSheetColumnValidationErrorType.Mandatory, errorCode: columnConfiguration.isMandatoryErrorCode, headerName, rowNumber };
            return result;
          }
          if (columnConfiguration.dataType && cellValue && !this.isDataType(cellValue, columnConfiguration.dataType)) {
            result.firstColumnValidationError = { errorType: ExcelSheetColumnValidationErrorType.DataType, errorCode: columnConfiguration.dataTypeErrorCode, headerName, rowNumber };
            return result;
          }
        }
      }
    }

    return result;
  }

  /**
   * Utility methods to verify if a spreadsheet cell contains a valid data type.
   * Valid data types that are currently supported are:
   * - String: a non-empty string
   *           (for example: "Hello", "world!", etc.)
   * - Boolean: a string containing true/false or yes/no
   *            (for example: "true", "TRUE", "yes", "YES", "false", "FALSE", "no", "NO")
   * - Number: a string containing any finite number, float or integer
   *            (for example: "0", "1", "-2", "123", "0.45", "-23.789", etc.)
   * - Integer: a string containing any finite integer number
   *            (for example: "0", "1", "-2", "123", etc.)
   * - Date: a string containing a date in the DD, MM, YYYY format with either "/" or "-" as separators and with or without the leading zero for day and month
   *         (for example: "31/12/1999", "01-01-2000", "02/1/2000", "3-01-2000", "3/1/2000", etc.)
   * - StringList: a non-empty string containing a pipe-separated list of strings
   *               (for example: "Apple|Orange|Banana", "black|white", "hello")
   */
  isString     = <TValue>(value: TValue): boolean => this.toString    (value) !== null;
  isBoolean    = <TValue>(value: TValue): boolean => this.toBoolean   (value) !== null;
  isNumber     = <TValue>(value: TValue): boolean => this.toNumber    (value) !== null;
  isInteger    = <TValue>(value: TValue): boolean => this.toInteger   (value) !== null;
  isDate       = <TValue>(value: TValue): boolean => this.toDate      (value) !== null;
  isStringList = <TValue>(value: TValue): boolean => this.toStringList(value) !== null;
  isDataType = <TValue>(value: TValue, type: ExcelSheetColumnDataType): boolean => ({
    [ExcelSheetColumnDataType.String    ]: v => this.isString    (v),
    [ExcelSheetColumnDataType.Boolean   ]: v => this.isBoolean   (v),
    [ExcelSheetColumnDataType.Number    ]: v => this.isNumber    (v),
    [ExcelSheetColumnDataType.Integer   ]: v => this.isInteger   (v),
    [ExcelSheetColumnDataType.Date      ]: v => this.isDate      (v),
    [ExcelSheetColumnDataType.StringList]: v => this.isStringList(v)
  })[type](value);

  /**
   * Utility methods to convert the value of a spreadsheet cell to a valid data type
   * See previous isXXX methods for a list of valid data types that are currently supported.
   * NOTE: if the conversion fails, the method returns null
   */
  toString     = <TValue>(value: TValue): string   => this.parseString(value, true);
  toBoolean    = <TValue>(value: TValue): boolean  => this.parseBoolean(value, ['FALSE', 'NO'], ['TRUE', 'YES']);
  toNumber     = <TValue>(value: TValue): number   => this.parseNumber(value, false);
  toInteger    = <TValue>(value: TValue): number   => this.parseNumber(value, true);
  toDate       = <TValue>(value: TValue): string   => this.parseDate(value, '/', 'yyyy-MM-dd') ?? this.parseDate(value, '-', 'yyyy-MM-dd');
  toStringList = <TValue>(value: TValue): string[] => this.parseStringList(value, '|', true);

  /**
   * Checks if an array of items contains "duplicates".
   * The definition of "duplicate" is dependent on the specific function provided which lists the item properties to be used for the comparison.
   * The properties are compared ignoring any leading/trailing spaces and ignoring letter casing, i.e. " sOME valuE  " is considered equal to "SOME VALUE".
   * @param items array of items to be checked for duplicates
   * @param getProperties function returning the array of string properties to be used for the duplicate comparison
   * @returns true if the array contains duplicates, false otherwise
   */
  hasDuplicateProperties<TItem>(items: TItem[], getProperties: (item: TItem) => string[]): boolean {
    const preProcessProperty = (property: string): string => property?.trim()?.toUpperCase();
    const getUniqueItemKey = (item: TItem): string => this.getHashCode(getProperties(item)?.map(preProcessProperty));
    return this.hasDuplicates(items, getUniqueItemKey);
  }

  // **********************************************************************************************************************************************************
  // PRIVATE
  // **********************************************************************************************************************************************************

  /**
   * Parses an Excel workbook using the XLSX third-party library.
   * The workbook is parsed as "raw", i.e. without trying to guess the data types of each cell, therefore each cell has a string type, regardless of the data type it contains
   * @param data file data containing the Excel workbook
   * @returns an XLSX.WorkSheet object containing the first worksheet found in the input workbook
   */
  private getXlsxWorkSheet(data: SheetData): XLSX.WorkSheet {
    try {
      const xlsxWorkBook = XLSX.read(data, { type: 'binary', raw: true });
      const xlsxWorkSheet = xlsxWorkBook.Sheets[xlsxWorkBook.SheetNames[0]];
      return xlsxWorkSheet;
    }
    catch {
      return null;
    }
  }

  /**
   * Utility methods to convert a value to a valid data type
   * See previous isXXX methods for a list of valid data types that are currently supported.
   * NOTE: if the conversion fails, the method returns null
   */
  private parseString<TValue>(value: TValue, trim: boolean): string {
    const s = value?.toString();
    return trim ? s?.trim() : s;
  }
  private parseBoolean<TValue>(value: TValue, falseLabels: string[], trueLabels: string[]): boolean {
    const s = value?.toString()?.trim()?.toUpperCase();
    return falseLabels.includes(s)
      ? false
      : (trueLabels.includes(s) ? true : null);
  }
  private parseNumber<TValue>(value: TValue, allowOnlyIntegers: boolean): number {
    const f = Number.parseFloat(value?.toString()?.trim());
    const n = !isNaN(f) ? f : null;
    return allowOnlyIntegers
      ? (Number.isInteger(n) ? n : null)
      : n;
  }
  private parseDate<TValue>(value: TValue, separator: string, outputFormat: string): string {
    const array = value?.toString()?.trim()?.split(separator)
      ?.map(e => this.toNumber(e))
      ?.filter(e => e !== null);
    const dateTime = array?.length === 3
      ? DateTime.fromObject({ day: array[0], month: array[1], year: array[2] })
      : null;
    return dateTime?.isValid
      ? dateTime.toFormat(outputFormat)
      : null;
  }
  private parseStringList<TValue>(value: TValue, separator: string, removeEmptyItems: boolean): string[] {
    const items = value?.toString()?.split(separator)
        ?.filter(item => !removeEmptyItems || item?.trim())
      ?? [];
    return items.length ? items : null;
  }

  /**
   * Calculates a unique hash code for a given value.
   * To calculate the hash, it simply performs a JSON serialisation and returns the JSON string as the hash code.
   * It is not exactly the best-performing algorithm to get a unique hash code, but it is good enough for our purposes.
   * @param value input value to be hashed
   * @returns string containing the hash code of the input value provided
   */
  private getHashCode = <TValue>(value: TValue): string => JSON.stringify(value);

  /**
   * Checks if an array of items contains "duplicates".
   * The definition of "duplicate" is dependent on the specific function provided to calculate a unique hash for each item
   * The algorithm has an O(n*log(n)) time complexity, as opposed to the the trivial "double nested loop" implementation, which has an O(n^2) time complexity.
   * @param items array of items to be checked for duplicates
   * @param getUniqueItemKey hash function used to compare items for equality
   * @returns true if the array contains duplicates, false otherwise
   */
  private hasDuplicates<TItem, TKey>(items: TItem[], getUniqueItemKey: (item: TItem) => TKey): boolean {
    if (items?.length ?? 0 > 0) {
      const map = new Map<TKey, TItem>();
      for (const item of items) {
        const uniqueItemKey = getUniqueItemKey(item);
        if (map.has(uniqueItemKey)) {
          return true;
        }
        map.set(uniqueItemKey, item);
      }
    }
    return false;
  }

  /**
   * Parses an Excel workbook using the XLSX third-party library.
   * The workbook is parsed as "raw", i.e. without trying to guess the data types of each cell, therefore each cell has a string type, regardless of the data type it contains
   * @param data file data containing the Excel workbook
   * @returns an array of objects, each containing the worksheet name and the XLSX.WorkSheet object, for all worksheets found in the input workbook
   */
  private getXlsxWorkSheets(data: SheetData): { xlsxWorkSheetName: string, xlsxWorkSheetData: string[][] }[] {
    try {
      const xlsxWorkBook = this.getXlsxWorkBook(data);
      const xlsxWorkSheets = xlsxWorkBook.SheetNames
        .map(xlsxWorkSheetName => {
          const xlsxWorkSheet = xlsxWorkBook.Sheets[xlsxWorkSheetName];
          const xlsxWorkSheetData = XLSX.utils.sheet_to_json(xlsxWorkSheet, { header: 1 }) as string[][];
          return { xlsxWorkSheetName, xlsxWorkSheetData };
        });
      return xlsxWorkSheets ?? [];
    }
    catch {
      return [];
    }
  }

  /**
   * Parses an Excel workbook using the XLSX third-party library.
   * The workbook is parsed as "raw", i.e. without trying to guess the data types of each cell, therefore each cell has a string type, regardless of the data type it contains
   * @param data file data containing the Excel workbook
   * @returns an XLSX.WorkBook object containing the input workbook
   */
  private getXlsxWorkBook(data: SheetData): XLSX.WorkBook {
    try {
      const xlsxWorkBook = XLSX.read(data, { type: 'binary', raw: true });
      return xlsxWorkBook;
    }
    catch {
      return null;
    }
  }
}
